import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { hasInternalApi, internalApiGet, internalApiPatch } from './internalApi'
import { supabase, type Manager } from './supabase'

type ManagerResponse = {
  manager: Manager
}

/**
 * Fetches the `managers` row that's linked to the currently signed-in
 * auth.users record (via the `handle_new_auth_user` trigger).
 *
 * If no row is found yet (e.g. trigger hasn't fired), returns null and
 * the caller renders a guidance message.
 */
export function useMyManager() {
  return useQuery<Manager | null>({
    queryKey: ['me', 'manager'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const response = await internalApiGet<ManagerResponse>('/api/me')
        return response.manager
      }

      const { data: auth } = await supabase.auth.getUser()
      if (!auth?.user) return null
      const { data, error } = await supabase
        .from('managers')
        .select('*')
        .eq('user_id', auth.user.id)
        .maybeSingle()
      if (error) throw error
      return data as Manager | null
    }
  })
}

/**
 * Updates `name` and `department` on the current manager row.
 */
export function useUpdateMyManager() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: { name?: string | null; department?: string | null }) => {
      if (hasInternalApi()) {
        const response = await internalApiPatch<ManagerResponse>('/api/me', patch)
        return response.manager
      }

      const { data: auth } = await supabase.auth.getUser()
      if (!auth?.user) throw new Error('Not signed in')
      const { data, error } = await supabase
        .from('managers')
        .update(patch)
        .eq('user_id', auth.user.id)
        .select()
        .single()
      if (error) throw error
      return data as Manager
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'manager'] })
    }
  })
}

/**
 * Updates the auth user's password via Supabase Auth.
 * The user must already be signed in.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (newPassword: string) => {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
    }
  })
}
